<template>
    <div>
        <ProductionProductPropertyValueDialog
            :is-open="productionProductPropertyValueDialog"
            :production-product-property-value-data="currentProductionProductPropertyValue"
            :available-product-production-properties-data="availableProductProductionProperties"
            :product-property-value="currentProductPropertyValue"
            :action-loading="actionLoading.saveProductPropertyValue"
            @on-save="saveProductionProductPropertyValue"
            @on-close="closeProductionProductPropertyValueDialog"
        />

        <ConfirmDialog
            :is-open="confirmDeleteProductionProductPropertyValueDialog"
            :data="currentProductionProductPropertyValue"
            :action-loading="actionLoading.saveProductPropertyValue"
            @on-confirm="deleteProductionProductPropertyValue"
            @on-abort="closeConfirmDeleteProductionProductPropertyValueDialog"
        >
            <template #title>
                Usunąć definicję cechy produkcyjnej?
            </template>
            <template #content>
                Czy na pewno chcesz usunąć defincję?
            </template>
        </ConfirmDialog>

        <v-container v-if="productPropertyValues.length === 0">
            <v-alert
                type="info"
                text
                border="left"
                class="mb-0"
            >
                Produkt nie posiada przypisanych cech sklepowych.
            </v-alert>
        </v-container>
        <v-container v-else class="pa-0">
            <v-row>
                <v-col cols="12" md="4">
                    <v-navigation-drawer
                        permanent
                        width="100%"
                    >
                        <v-autocomplete
                            v-model="productPropertyFilter"
                            :items="productProperties"
                            item-value="id"
                            item-text="name"
                            label="Cecha nadrzędna"
                            outlined
                            dense
                            no-data-text="Nie znaleziono"
                            clearable
                            hide-details
                            return-object
                            class="mx-4 mt-2"
                            @change="changeProductPropertyFilter"
                        />
                        <v-list two-line max-height="400" class="overflow-y-auto">
                            <v-list-item-group
                                :value="selectedProductPropertyValueId"
                                color="primary"
                                @change="changePropertyValue"
                            >
                                <v-list-item
                                    v-for="(productPropertyValue) in filteredProductPropertyValuesList"
                                    :key="productPropertyValue.id"
                                    :value="productPropertyValue.id"
                                    @click="selectPropertyValue(productPropertyValue)"
                                >
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ productPropertyValue.productProperty.name }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle>
                                            {{ productPropertyValue.name }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-badge
                                            v-if="!productPropertyValue.hasProductionPropertyValues"
                                            content="nowa"
                                            inline
                                            color="secondary"
                                        />
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </v-navigation-drawer>
                </v-col>
                <v-col cols="12" md="8">
                    <v-btn
                        color="secondary"
                        medium
                        depressed
                        class="mb-4 mt-2"
                        data-sentry-label="Dodaj definicję"
                        :disabled="!isProductPropertyValueSelected"
                        @click="showProductionProductPropertyValueDialog()"
                    >
                        <v-icon left>
                            mdi-plus
                        </v-icon>
                        Dodaj definicję
                    </v-btn>

                    <v-data-table
                        :headers="productionProductTypePropertyValuesHeaders"
                        :items="productionProductTypePropertyValues"
                        hide-default-footer
                        disable-pagination
                        :mobile-breakpoint="0"
                        :loading="actionLoading.getProductPropertyValues"
                        name="productionProductTypePropertyValuesData"
                    >
                        <template #item.productProductionPropertyName="{item}">
                            {{ item.productProductionProperty.verboseName }}
                        </template>
                        <template #item.productProductionPropertyValue="{item}">
                            {{ item.productProductionPropertyValue }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Edytuj definicję"
                                            v-on="on"
                                            @click="showProductionProductPropertyValueDialog(item)"
                                        >
                                            <v-icon color="teal">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edytuj</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń definicję"
                                            v-on="on"
                                            @click="showConfirmDeleteProductionProductPropertyValueDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import ProductionProductPropertyValueDialog from './ProductionProductPropertyValueDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'

import { errorHandler } from '@/utils/errorHandler'
import tooltipDelay from '@/mixins/tooltip'

export default {
    name: 'ProductProperties',
    components: {
        ProductionProductPropertyValueDialog, ConfirmDialog
    },
    mixins: [
        tooltipDelay
    ],
    props: {
        productId: {
            type: Number,
            default: null
        },
        productProperties: {
            type: Array,
            default: () => []
        },
        availableProductProductionProperties: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            productPropertyValues: [],
            filteredProductPropertyValuesList: [],
            selectedProductPropertyValueId: null,
            currentProductPropertyValue: null,
            currentProductionProductPropertyValue: null,
            productPropertyFilter: null,
            productionProductTypePropertyValues: [],
            productionProductPropertyValueDialog: false,
            confirmDeleteProductionProductPropertyValueDialog: false,
            actionLoading: {
                getProductPropertyValues: false,
                saveProductPropertyValue: false
            },
            productionProductTypePropertyValuesHeaders: [
                {
                    text: 'Cecha produkcyjna',
                    value: 'productProductionPropertyName',
                    sortable: false,
                    width: '50%'
                },
                {
                    text: 'Wartość',
                    value: 'productProductionPropertyValue',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        isProductPropertyValueSelected() {
            return this.selectedProductPropertyValueId !== null
        },
        hasUnsolvedProductProperties() {
            return this.productPropertyValues.some(item => !item.hasProductionPropertyValues)
        },
    },
    watch: {
        hasUnsolvedProductProperties(value) {
            this.$emit('on-status-change', value)
        }
    },
    mounted() {
        this.getProductPropertyValuesByProductType(this.productId)
            .then(() => {
                this.filteredProductPropertyValuesList = this.productPropertyValues
            })
            .catch(error => errorHandler(error))
    },
    methods: {
        async getProductPropertyValuesByProductType(productTypeId) {
            return await this.$store.dispatch('configurations/getProductPropertyValuesByProductType', productTypeId)
                .then(response => {
                    this.productPropertyValues = response.data.results

                    return Promise.resolve(response)
                })
                .catch(error => {
                    return Promise.reject(error)
                })
        },
        async getProductionProductTypePropertyValues(productPropertyValueJoinId) {
            return await this.$store.dispatch('configurations/getProductionProductTypePropertyValues', productPropertyValueJoinId)
                .then(response => {
                    return Promise.resolve(response)
                })
                .catch(error => {
                    return Promise.reject(error)
                })
        },
        changeProductPropertyFilter(data) {
            if (data) {
                this.filteredProductPropertyValuesList = this.productPropertyValues.filter(item => {
                    return item.productProperty.id === data.id
                })
            } else {
                this.filteredProductPropertyValuesList = this.productPropertyValues
            }
        },
        changePropertyValue(productPropertyValueId) {
            if (typeof productPropertyValueId === 'undefined') {
                this.selectedProductPropertyValueId = null
            } else {
                this.selectedProductPropertyValueId = productPropertyValueId
            }
        },
        selectPropertyValue(productPropertyValue) {
            this.setCurrentProductPropertyValue(productPropertyValue)

            this.$nextTick(() => {
                this.setCurrentProductPropertyValue(productPropertyValue)

                if (this.isProductPropertyValueSelected) {
                    this.actionLoading.getProductPropertyValues = true
                    this.getProductionProductTypePropertyValues(productPropertyValue.productPropertyValueJoinId)
                        .then(response => {
                            this.productionProductTypePropertyValues = response.data.results
                        })
                        .catch(error => errorHandler(error))
                        .finally(() => {
                            this.actionLoading.getProductPropertyValues = false
                        })
                }
            })
        },
        setCurrentProductPropertyValue(productPropertyValue) {
            if (this.isProductPropertyValueSelected) {
                this.currentProductPropertyValue = productPropertyValue
            } else {
                this.currentProductPropertyValue = null
                this.productionProductTypePropertyValues = []
            }
        },
        showProductionProductPropertyValueDialog(data) {
            this.productionProductPropertyValueDialog = true
            this.currentProductionProductPropertyValue = data
        },
        saveProductionProductPropertyValue(data, isNewProductionProductPropertyValue) {
            this.actionLoading.saveProductPropertyValue = true

            if (isNewProductionProductPropertyValue) {
                this.$store.dispatch('configurations/createProductionProductTypePropertyValue', data)
                    .then(response => {
                        Promise.all([
                            this.getProductPropertyValuesByProductType(this.productId),
                            this.getProductionProductTypePropertyValues(response.data.productPropertyValueJoinId)
                        ])
                            .then(responses => {
                                this.productionProductTypePropertyValues = responses[1].data.results
                                this.changeProductPropertyFilter(this.productPropertyFilter)
                                this.closeProductionProductPropertyValueDialog()
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                            .finally(() => {
                                this.actionLoading.saveProductPropertyValue = false
                            })
                    })
                    .catch(error => {
                        this.actionLoading.saveProductPropertyValue = false
                        errorHandler(error)
                    })
            } else {
                this.$store.dispatch('configurations/updateProductionProductTypePropertyValue', data)
                    .then(response => {
                        Promise.all([
                            this.getProductPropertyValuesByProductType(this.productId),
                            this.getProductionProductTypePropertyValues(response.data.productPropertyValueJoinId)
                        ])
                            .then(responses => {
                                this.productionProductTypePropertyValues = responses[1].data.results
                                this.changeProductPropertyFilter(this.productPropertyFilter)
                                this.closeProductionProductPropertyValueDialog()
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                            .finally(() => {
                                this.actionLoading.saveProductPropertyValue = false
                            })
                    })
                    .catch(error => {
                        this.actionLoading.saveProductPropertyValue = false
                        errorHandler(error)
                    })
            }
        },
        closeProductionProductPropertyValueDialog() {
            this.productionProductPropertyValueDialog = false
            setTimeout(() => {
                this.currentProductionProductPropertyValue = null
            }, 500)
        },
        showConfirmDeleteProductionProductPropertyValueDialog(data) {
            this.confirmDeleteProductionProductPropertyValueDialog = true
            this.currentProductionProductPropertyValue = data
        },
        deleteProductionProductPropertyValue(productionProductPropertyValue) {
            this.actionLoading.saveProductPropertyValue = true
            this.$store.dispatch('configurations/deleteProductionProductTypePropertyValue', productionProductPropertyValue.id)
                .then(() => {
                    Promise.all([
                        this.getProductPropertyValuesByProductType(this.productId),
                        this.getProductionProductTypePropertyValues(this.currentProductionProductPropertyValue.productPropertyValueJoinId)
                    ])
                        .then(responses => {
                            this.productionProductTypePropertyValues = responses[1].data.results
                            this.changeProductPropertyFilter(this.productPropertyFilter)
                            this.closeConfirmDeleteProductionProductPropertyValueDialog()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.actionLoading.saveProductPropertyValue = false
                        })
                })
                .catch(error => {
                    this.actionLoading.saveProductPropertyValue = false
                    errorHandler(error)
                })
        },
        closeConfirmDeleteProductionProductPropertyValueDialog() {
            this.confirmDeleteProductionProductPropertyValueDialog = false
            setTimeout(() => {
                this.currentProductionProductPropertyValue = null
            }, 500)
        }
    }
}
</script>